/**
 * Policy-aware order columns for the /orders/school grid.
 *
 * Columns derive from the campus's effective ORDER grouping dimension
 * (`orderGroupBy`): AGE_GROUP campuses order by age-group column
 * (`LocationGradeRange`); NONE campuses order as one whole school — a
 * single synthetic bucket-less column (`WHOLE_SCHOOL_COLUMN_ID`) whose
 * lines carry no group tuple (portion tier optional via
 * `school.wholeSchool`); every other dimension (GRADE / TEACHER / ROOM /
 * HOMEROOM / LUNCH_PERIOD) orders by the campus vocabulary values the
 * server projects into `orderGroups`, identified by their
 * `(groupDimension, groupValue)` tuple. `OrderColumn` is the common
 * projection both render through, so the day table, collapsed summary,
 * checkout matrix, and cart lines stay dimension-agnostic:
 *
 *   - `columnId`   — the cart-line key segment (age-group column id or
 *                    the tuple key).
 *   - `audienceId` — the id already-ordered items are keyed by: the
 *                    portion tier (`GradeRange.id`) for age-group columns
 *                    (two columns may share a tier), the tuple key for
 *                    every other dimension.
 *   - `groupDimension`/`groupValue` — the tuple sent on cart lines for
 *                    non-AGE_GROUP columns; `gradeRangeId` covers the
 *                    AGE_GROUP portion tier.
 *
 * Pure (no React) — unit-tested in `client/test/lib/school-orders/`.
 */

import { formatGradeRangeCodeLabel } from '@ordo/constants/grade-matcher'
import { hasGradeRangeCode, menuAllowsGradeRangeCode } from '@ordo/constants/menu-grade-range-gate'
import {
  bandCoversGradeRangeCode,
  bandFoldColumnIds,
  ORDER_PORTION_BAND_CODES,
  type OrderBandMealtimeStyle,
  type OrderBandPartition,
  orderBandPartition,
  orderBandStyleForMealtime,
  type OrderPortionBandCode,
  orderPortionBandsForMenuCode,
  orderPortionBandsForMenuCodes,
} from '@ordo/constants/portion-groups'

import type { AdminSchool, AdminSchoolGroupColumn } from '@/api/school-orders'

/**
 * Sentinel `columnId`/`audienceId` for the single synthetic column a
 * NONE (whole-school) campus orders through. Can't collide with real
 * ids: age-group column ids are cuids and tuple keys contain `::`.
 */
export const WHOLE_SCHOOL_COLUMN_ID = 'WHOLE_SCHOOL'

export interface OrderColumn {
  /** Cart-line key segment — LocationGradeRange.id or the tuple key. */
  columnId: string
  label: string
  /** Secondary header line; currently null for every column kind. */
  sublabel: string | null
  /** Current-term enrolled count ?? planning population; null = unknown. */
  studentCount: number | null
  /**
   * Already-ordered lookup key: portion tier for age-group columns,
   * `${groupDimension}::${groupValue}` for group columns.
   */
  audienceId: string
  /**
   * Portion tier — set for age-group columns and (optionally) the
   * whole-school column, null otherwise.
   */
  gradeRangeId: string | null
  /**
   * Portion-tier band code ("k-5", "6-8", …) — always set for age-group
   * columns; set for group columns whose vocabulary row resolved a tier,
   * null otherwise. Drives the per-column menu grade-band gate.
   */
  gradeRangeCode: string | null
  /**
   * The `(groupDimension, groupValue)` column identity for non-AGE_GROUP
   * campuses; null for AGE_GROUP columns.
   */
  groupDimension: AdminSchoolGroupColumn['groupDimension'] | null
  groupValue: string | null
  sortOrder: number
  /**
   * EFFECTIVE USDA service style for this column (column override → campus
   * → org, resolved server-side). Component (%) ordering is gated per
   * column: an SS column always orders 100% of every component even on a
   * campus whose other columns run OVS. Non-AGE_GROUP columns carry the
   * campus style. `null` = unprovisioned (treated SS).
   */
  serviceStyle: 'OVS' | 'SS' | null
  /**
   * Menu-derived BAND / ALL_GRADES columns only: the campus
   * `LocationGradeRange` row ids whose tiers fold into this column, MOST
   * PREFERRED FIRST (`bandFoldColumnIds` — campus sortOrder, then tier code,
   * then id). Two jobs:
   *
   *  - field-trip item scopes (stored as LGR ids) keep matching after the
   *    column identity moved off the campus rows, and
   *  - per-column RATE / MILK-SHARE config, which is stored under these same
   *    LGR ids by the settings surfaces, resolves for this band column —
   *    `milkSharePctForColumn` / `effectiveRatesFor` take the list and fall
   *    back to the first entry with a stored row (see `foldedColumnKeyFor`).
   *
   * The ORDER is load-bearing for the second job: it decides which campus
   * column's numbers a band column inherits when several fold in, and it is
   * the same ordering the server's `band-column-fold.ts` applies.
   */
  coveredLocationGradeRangeIds?: string[]
}

/** A menu item's group-scope gate fields off the menus wire. */
export interface ItemColumnScope {
  /** Age-group scope — LocationGradeRange column ids. Empty = none. */
  allowedLocationGradeRangeIds: string[]
  /** Tuple scopes (`{ dimension, value }`); null/empty = none. */
  allowedGroupScopes: Array<{ dimension: string; value: string }> | null
  /**
   * The owning MENU's grade-range tags (`orderableMenusFor` attaches
   * them). Empty/absent = untagged menu → every column. Optional so
   * pre-existing wire shapes (family surfaces) type-check unchanged.
   */
  menuGradeRangeCodes?: readonly string[] | null
}

/**
 * Column hard-gate. BOTH gates must pass:
 *
 * 1. Menu grade band — the item's MENU must serve the column's portion
 *    band (`menuGradeRangeCodes` vs `OrderColumn.gradeRangeCode`, same
 *    matching rule as the family menu filter). An untagged menu passes
 *    (pre-tagging back-compat). A band-less column — the NONE whole-school
 *    column and group columns whose vocab row resolved no tier — is
 *    band-agnostic BY DESIGN and skips the gate via `hasGradeRangeCode`
 *    (the gate itself is fail-closed for unknown bands since 2026-07).
 * 2. Item scope (field-trip bagged lunches) — age-group columns match by
 *    LocationGradeRange id (either the legacy id list or an AGE_GROUP
 *    tuple scope); group columns match their `(groupDimension,
 *    groupValue)` tuple against `allowedGroupScopes` (value labels
 *    compared case-insensitively). Empty scopes = unrestricted.
 */
export function isItemAllowedForColumn(
  item: ItemColumnScope,
  column: Pick<
    OrderColumn,
    'columnId' | 'gradeRangeCode' | 'groupDimension' | 'groupValue' | 'coveredLocationGradeRangeIds'
  >,
): boolean {
  if (
    hasGradeRangeCode(column.gradeRangeCode) &&
    !menuAllowsGradeRangeCode(item.menuGradeRangeCodes ?? [], column.gradeRangeCode)
  ) {
    return false
  }
  const scopes = item.allowedGroupScopes ?? []
  if (item.allowedLocationGradeRangeIds.length === 0 && scopes.length === 0) {
    return true
  }
  if (column.groupDimension && column.groupValue) {
    const value = column.groupValue.trim().toLowerCase()
    return scopes.some(
      (scope) => scope.dimension === column.groupDimension && scope.value.trim().toLowerCase() === value,
    )
  }
  const columnIds = [column.columnId, ...(column.coveredLocationGradeRangeIds ?? [])]
  return (
    columnIds.some((id) => item.allowedLocationGradeRangeIds.includes(id)) ||
    scopes.some((scope) => scope.dimension === 'AGE_GROUP' && columnIds.includes(scope.value))
  )
}

/** The campus's effective ORDER grouping dimension. */
export function orderColumnsGroupBy(school: Pick<AdminSchool, 'orderGroupBy'>): AdminSchool['orderGroupBy'] {
  return school.orderGroupBy ?? 'AGE_GROUP'
}

/**
 * The school's order columns in canonical display order. AGE_GROUP: the
 * grade-range mapping the grid has always rendered (sortOrder, then
 * canonical range name). NONE: exactly one synthetic "Whole school"
 * column (bucket-less — null tuple, optional portion tier from
 * `school.wholeSchool`). Every other dimension: the campus vocabulary
 * values as the server ordered them (sortOrder, then name).
 */
export function buildOrderColumns(school: AdminSchool): OrderColumn[] {
  const groupBy = orderColumnsGroupBy(school)
  if (groupBy === 'NONE') {
    return [
      {
        columnId: WHOLE_SCHOOL_COLUMN_ID,
        audienceId: WHOLE_SCHOOL_COLUMN_ID,
        label: 'Whole school',
        sublabel: null,
        studentCount: school.wholeSchool?.studentCount ?? null,
        gradeRangeId: school.wholeSchool?.gradeRangeId ?? null,
        // Band-less on purpose: a whole-school column spans every portion
        // band, so the per-column menu grade-band gate never blocks it.
        gradeRangeCode: null,
        groupDimension: null,
        groupValue: null,
        sortOrder: 0,
        // Whole-school column has no per-column override — campus style.
        serviceStyle: school.serviceStyle,
      },
    ]
  }
  if (groupBy !== 'AGE_GROUP') {
    return (school.orderGroups ?? []).map((g) => ({
      columnId: g.id,
      label: g.name,
      sublabel: null,
      studentCount: g.studentCount,
      audienceId: `${g.groupDimension}::${g.groupValue}`,
      gradeRangeId: null,
      // Group columns still gate by menu band when their vocabulary row
      // resolved a portion tier (a HOMEROOM mapped to K-5 shouldn't order
      // off a 9-12 menu); unresolved rows stay ungated.
      gradeRangeCode: g.gradeRange?.code ?? null,
      groupDimension: g.groupDimension,
      groupValue: g.groupValue,
      sortOrder: g.sortOrder,
      // Vocabulary columns have no per-column override — campus style.
      serviceStyle: school.serviceStyle,
    }))
  }
  return [...school.gradeRanges]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.gradeRange.name.localeCompare(b.gradeRange.name))
    .map((gr) => ({
      columnId: gr.id,
      // Column label: per-campus custom name wins; otherwise the canonical
      // code label (so 9-12 reads "9-12", not "High"). Only @ordo/* runtime
      // imports here so the module loads under bare `bun test`.
      label: gradeRangeColumnLabel(gr),
      sublabel: null,
      studentCount: gr.studentCount,
      audienceId: gr.gradeRange.id,
      gradeRangeId: gr.gradeRange.id,
      gradeRangeCode: gr.gradeRange.code,
      groupDimension: null,
      groupValue: null,
      sortOrder: gr.sortOrder,
      // Server-resolved effective service style for the column (column
      // override → campus → org); legacy payloads without the field inherit
      // campus.
      serviceStyle: gr.serviceStyle ?? school.serviceStyle,
    }))
}

function gradeRangeColumnLabel(row: AdminSchool['gradeRanges'][number]): string {
  const codeLabel = formatGradeRangeCodeLabel(row.gradeRange.code)
  const customName = row.customName?.trim()
  if (customName && customName.toLowerCase() !== row.gradeRange.name.trim().toLowerCase()) {
    return customName
  }
  return codeLabel ?? row.gradeRange.name
}

/**
 * True when ANY order column of the campus orders by component — the gate
 * for fetching component-delivery rates / milk shares and for showing the
 * order-sheet prefill header on mixed-style campuses.
 */
export function anyColumnOrdersByComponent(school: AdminSchool): boolean {
  return school.serviceStyle === 'OVS' || buildOrderColumns(school).some((c) => c.serviceStyle === 'OVS')
}

/**
 * WHICH settings surface owns the campus's milk-split editor. Exactly one may
 * render (two editors over the same `MilkShareRate` rows would fight).
 *
 * The rule: **milk is editable whenever the campus has a milk lineup**, no
 * matter what the component split is doing. The Pre-fill card carries the milk
 * grid only INSIDE its per-mealtime split dialog, so the question is simply
 * whether that dialog is reachable.
 *
 * Since 2026-08-26 it always is for a by-component campus: splits are always on
 * for OVS (Sheila — *"That shouldn't be possible if it's OVS, they just need to
 * update to 100%"*), the on/off switch is gone, and the per-mealtime Edit
 * dialog renders whatever the campus's stored mode says. So the answer no
 * longer depends on the split state — which also retires the PENDING state that
 * existed only to keep the milk card from flashing while order preferences
 * loaded.
 *
 * The earlier version of this gate is still worth remembering: while the split
 * COULD be off, the dialog was unreachable, and a campus with the split off had
 * no milk editor at all even though it still derives one milk per meal.
 */
export function milkEditorSurface(args: {
  /** `anyColumnOrdersByComponent(school)`. */
  ordersByComponent: boolean
}): 'PREFILL_CARD' | 'MILK_CARD' {
  return args.ordersByComponent ? 'PREFILL_CARD' : 'MILK_CARD'
}

/** True when the school has at least one order column to render. */
export function hasOrderColumns(school: AdminSchool): boolean {
  return buildOrderColumns(school).length > 0
}

// ---------------------------------------------------------------------------
// Locked portion-band columns (2026-08-22): AGE_GROUP campuses order in the
// bands the day's MENUS serve, per the program-keyed partition in
// @ordo/constants/portion-groups (`orderBandPartition` — the ONE seam a
// future per-program Grade Mapping admin config will replace):
//   pattern-bound orgs: PK | K-8 | 9-12 (lunch-style), PK | K-12 (breakfast)
//   PRIVATE orgs: one band-less column per mealtime, PK folds in.
// ---------------------------------------------------------------------------

/**
 * Sentinel columnId/audienceId for the ONE band-less column a PRIVATE
 * (non-pattern-bound) AGE_GROUP campus orders through, per mealtime. Same
 * shape as the whole-school sentinel: band-less by design (the menu grade
 * gate skips it), tier-less lines on the wire.
 */
export const ALL_GRADES_COLUMN_ID = 'ALL_GRADES'

/** The campus tier rows folding into one locked band. */
function coveredCampusTiers(school: AdminSchool, band: OrderPortionBandCode): AdminSchool['gradeRanges'] {
  return school.gradeRanges.filter((gr) => bandCoversGradeRangeCode(band, gr.gradeRange.code))
}

function summedStudentCount(covered: AdminSchool['gradeRanges']): number | null {
  const counts = covered
    .map((gr) => gr.studentCount)
    .filter((count): count is number => count !== null && count !== undefined)
  return counts.length > 0 ? counts.reduce((sum, count) => sum + count, 0) : null
}

function coveredServiceStyle(school: AdminSchool, covered: AdminSchool['gradeRanges']): 'OVS' | 'SS' | null {
  const styles = [...new Set(covered.map((gr) => gr.serviceStyle ?? school.serviceStyle))]
  return styles.length === 1 ? (styles[0] ?? school.serviceStyle) : school.serviceStyle
}

/** One locked-band order column, or null when the band row isn't seeded. */
function bandColumn(school: AdminSchool, band: OrderPortionBandCode, sortOrder: number): OrderColumn | null {
  const bandRow = (school.portionBands ?? []).find((row) => row.code === band)
  if (!bandRow) {
    return null
  }
  const covered = coveredCampusTiers(school, band)
  return {
    // The canonical band tier id is the whole column identity: cart-line
    // key segment, already-ordered audience, and the stamped portion tier.
    columnId: bandRow.id,
    audienceId: bandRow.id,
    gradeRangeId: bandRow.id,
    gradeRangeCode: band,
    label: formatGradeRangeCodeLabel(band) ?? bandRow.name,
    sublabel: null,
    // Enrollment folds with the tiers: the band's count is the sum of the
    // campus tiers it covers; null (unknown) when none carries a count.
    studentCount: summedStudentCount(covered),
    groupDimension: null,
    groupValue: null,
    sortOrder,
    // One covered style wins; mixed or uncovered bands fall back to campus.
    serviceStyle: coveredServiceStyle(school, covered),
    coveredLocationGradeRangeIds: foldOrderedIds(band, covered),
  }
}

/**
 * The covered campus column ids in FOLD order (`bandFoldColumnIds` — the same
 * ordering the server's `band-column-fold.ts` uses), so a band column and the
 * server can never disagree about which campus column's stored rates/shares it
 * inherits.
 */
function foldOrderedIds(band: OrderPortionBandCode | null, covered: AdminSchool['gradeRanges']): string[] {
  return bandFoldColumnIds(
    band,
    covered.map((gr) => ({ columnId: gr.id, gradeRangeCode: gr.gradeRange.code, sortOrder: gr.sortOrder })),
  )
}

/** The PRIVATE campus's one band-less column (PK folds in). */
function allGradesColumn(school: AdminSchool): OrderColumn {
  return {
    columnId: ALL_GRADES_COLUMN_ID,
    audienceId: ALL_GRADES_COLUMN_ID,
    // Tier-less on purpose: no federal pattern binds a private program, so
    // the line carries no portion tier and the band gate skips the column.
    gradeRangeId: null,
    gradeRangeCode: null,
    label: 'All grades',
    sublabel: null,
    studentCount: summedStudentCount(school.gradeRanges),
    groupDimension: null,
    groupValue: null,
    sortOrder: 0,
    serviceStyle: school.serviceStyle,
    // Band-less: every campus column folds in, so per-column config stored
    // against any of them can resolve here too.
    coveredLocationGradeRangeIds: foldOrderedIds(null, school.gradeRanges),
  }
}

/** Absent `usdaPatternBound` (older payloads) reads pattern-bound — the conservative partition. */
function schoolPatternBound(school: AdminSchool): boolean {
  return school.usdaPatternBound !== false
}

const SHIPPED_PARTITION_KEY: Record<OrderBandMealtimeStyle, keyof NonNullable<AdminSchool['orderBandPartition']>> = {
  breakfast: 'breakfastStyle',
  lunch: 'lunchStyle',
  snack: 'snackStyle',
  supper: 'supperStyle',
}

/**
 * The partition for one mealtime: the SERVER-RESOLVED Grade Mapping
 * partition when the payload ships one (Menus -> Maintenance config over
 * shipped defaults), else the client-side constant fallback (stale caches,
 * older payloads).
 */
function schoolBandPartition(school: AdminSchool, mealtime: string | null | undefined): OrderBandPartition {
  const shipped = school.orderBandPartition
  if (shipped) {
    const styled = shipped[SHIPPED_PARTITION_KEY[orderBandStyleForMealtime(mealtime)]]
    if (styled?.kind === 'SINGLE') {
      return styled
    }
    if (styled?.kind === 'BANDS') {
      // Wire bands are plain strings — narrow to the known codes.
      const bands = ORDER_PORTION_BAND_CODES.filter((band) => styled.bands.includes(band))
      if (bands.length > 0) {
        return { kind: 'BANDS', bands }
      }
    }
  }
  return orderBandPartition({ patternBound: schoolPatternBound(school), mealtime })
}

/**
 * Order columns for ONE MEALTIME's table, derived from that mealtime's
 * menus: on an AGE_GROUP campus, the program-keyed partition filtered to
 * the bands any menu serves (an untagged menu spans every partition band —
 * same rule as the cell gate); a PRIVATE campus gets the single band-less
 * column whenever the mealtime has any menu. Every other grouping
 * dimension is untouched — group/whole-school columns are campus
 * vocabulary, not portion bands.
 *
 * Falls back to the campus-configured columns when the payload predates
 * `portionBands` (stale cache) so the sheet never renders empty.
 */
export function buildMenuOrderColumns(
  school: AdminSchool,
  mealtime: string | null,
  menuGradeRangeCodeSets: ReadonlyArray<readonly string[]>,
): OrderColumn[] {
  if (orderColumnsGroupBy(school) !== 'AGE_GROUP') {
    return buildOrderColumns(school)
  }
  if ((school.portionBands ?? []).length === 0) {
    return buildOrderColumns(school)
  }
  const partition = schoolBandPartition(school, mealtime)
  if (partition.kind === 'SINGLE') {
    return menuGradeRangeCodeSets.length > 0 ? [allGradesColumn(school)] : []
  }
  const reached = new Set(
    menuGradeRangeCodeSets.flatMap((codes) => orderPortionBandsForMenuCodes(codes, partition.bands)),
  )
  // Menu tags declare what a menu CAN serve (templates carry the union —
  // one template serves PK-7 and K-8 schools alike); the campus's
  // configured grade ranges declare what this school ACTUALLY serves. A
  // band column renders only in the INTERSECTION, so a union-tagged
  // template never materializes a phantom PK column at a K-12 school —
  // and republish can't reintroduce one (Sheila 2026-08-23). A campus
  // with no configured ranges keeps the menu-only derivation.
  const campusServes = (band: OrderPortionBandCode): boolean =>
    school.gradeRanges.length === 0 || coveredCampusTiers(school, band).length > 0
  return partition.bands
    .filter((band) => reached.has(band) && campusServes(band))
    .flatMap((band, index) => {
      const column = bandColumn(school, band, index)
      return column ? [column] : []
    })
}

/**
 * Checkout/review columns reconciled from ONE GROUP's cart lines: band /
 * all-grades columns for lines keyed to the new identities, campus-
 * configured columns for legacy line keys (a cart persisted before the
 * band cutover). New identities first, then legacy — a mixed cart renders
 * every line instead of dropping one identity.
 */
export function checkoutColumnsForLines(
  school: AdminSchool,
  lines: ReadonlyArray<{ columnId: string }>,
): OrderColumn[] {
  if (orderColumnsGroupBy(school) !== 'AGE_GROUP') {
    return buildOrderColumns(school)
  }
  const lineColumnIds = new Set(lines.map((line) => line.columnId))
  const candidates: OrderColumn[] = [
    ...(lineColumnIds.has(ALL_GRADES_COLUMN_ID) ? [allGradesColumn(school)] : []),
    ...ORDER_PORTION_BAND_CODES.flatMap((band, index) => {
      const column = bandColumn(school, band, index + 1)
      return column && lineColumnIds.has(column.columnId) ? [column] : []
    }),
  ]
  const legacyCols = buildOrderColumns(school).filter((column) => lineColumnIds.has(column.columnId))
  return [...candidates, ...legacyCols]
}

/**
 * Already-ordered audience fold for an AGE_GROUP campus: a placed line's
 * portion tier folds onto the audience its MEALTIME's partition renders —
 * band tier ids per the partition (legacy campus tiers AND narrow band ids
 * both fold, so k-8-stamped lunch lines surface under breakfast's K-12
 * column and vice versa never happens: the fold is mealtime-aware), or the
 * ALL_GRADES sentinel on a private campus. Identity for unmapped ids.
 */
export function bandAudienceFold(school: AdminSchool): (mealtime: string, tierId: string) => string {
  if (orderColumnsGroupBy(school) !== 'AGE_GROUP') {
    return (_mealtime, tierId) => tierId
  }
  const bands = school.portionBands ?? []
  const bandIdByCode = new Map(bands.map((row) => [row.code, row.id]))
  if (bandIdByCode.size === 0) {
    return (_mealtime, tierId) => tierId
  }
  // tier id → its NARROW band code ('pk' | 'k-8' | '9-12'), covering both
  // campus-configured tiers and the canonical band rows themselves.
  const narrowBandByTierId = new Map<string, OrderPortionBandCode>()
  for (const gr of school.gradeRanges) {
    const band = orderPortionBandsForMenuCode(gr.gradeRange.code)[0]
    if (band) {
      narrowBandByTierId.set(gr.gradeRange.id, band)
    }
  }
  for (const row of bands) {
    if (row.code === 'pk' || row.code === 'k-8' || row.code === '9-12') {
      narrowBandByTierId.set(row.id, row.code)
    }
  }
  const tierCodeById = new Map<string, string>([
    ...school.gradeRanges.map((gr) => [gr.gradeRange.id, gr.gradeRange.code] as const),
    ...bands.map((row) => [row.id, row.code] as const),
  ])
  return (mealtime, tierId) => {
    const partition = schoolBandPartition(school, mealtime)
    if (partition.kind === 'SINGLE') {
      return ALL_GRADES_COLUMN_ID
    }
    // Childcare partitions (Infant | Age 1-5): infant and age 1-5 tiers fold onto their band.
    const tierCode = tierCodeById.get(tierId)
    const childcareBand = partition.bands.find(
      (band) => (band === 'infant' || band === 'age-1-5') && bandCoversGradeRangeCode(band, tierCode),
    )
    if (childcareBand) {
      return bandIdByCode.get(childcareBand) ?? tierId
    }
    const narrow = narrowBandByTierId.get(tierId)
    if (!narrow) {
      return tierId
    }
    const target = partition.bands.includes(narrow)
      ? narrow
      : narrow !== 'pk' && partition.bands.includes('k-12')
        ? 'k-12'
        : narrow
    return bandIdByCode.get(target) ?? tierId
  }
}
